// Real API for Mechanical Room overview and equipment details
// This file contains actual HTTP requests to the Flask backend server
import API_BASE_URL from '../config/api';

/**
 * Health check endpoint to verify API connectivity and building access
 * 
 * Endpoint: POST /api/mechanical/health
 * 
 * Request Format:
 * {
 *   "buildingId": "string" (required) - The building ID to verify access
 * }
 * 
 * Headers:
 * - Content-Type: application/json
 * - Authorization: Bearer {token} (optional, if auth is required)
 * 
 * Response Format:
 * {
 *   "status": "ok" | "error",
 *   "message": "string" (e.g., "Mechanical API is operational for building {buildingId}"),
 *   "buildingId": "string" (the building ID that was checked),
 *   "timestamp": "string" (ISO 8601 timestamp, optional)
 * }
 * 
 * Status Codes:
 * - 200: API is healthy and responding, building access verified
 * - 400: Invalid buildingId
 * - 403: Access denied to building
 * - 500: Server error
 * - 503: Service unavailable
 */
export async function checkMechanicalApiHealth(buildingId) {
  const token = localStorage.getItem('managerToken');
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/mechanical/health`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: JSON.stringify({ buildingId })
    });

    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Mechanical API health check error:', error);
    throw error;
  }
}

/**
 * Get mechanical room overview for a building
 * 
 * Endpoint: POST /api/mechanical/overview
 * 
 * Request Format:
 * {
 *   "buildingId": "string" (required) - The building ID
 * }
 * 
 * Headers:
 * - Content-Type: application/json
 * - Authorization: Bearer {token} (optional, if auth is required)
 * 
 * Response Format:
 * {
 *   "summary": {
 *     "totalEquipment": number,
 *     "activeIssues": number,
 *     "resolvedIssues": number,
 *     "overallHealthPercent": number (0-100),
 *     "lastPmDate": "YYYY-MM-DD",
 *     "nextDueDate": "YYYY-MM-DD",
 *     "scheduleUrl": "string" (URL to maintenance schedule),
 *     "upcomingServiceDates": ["YYYY-MM-DD"] (array of dates)
 *   },
 *   "issues": [
 *     {
 *       "id": "string",
 *       "equipmentId": "string",
 *       "title": "string",
 *       "description": "string",
 *       "priority": "Low" | "Medium" | "High",
 *       "status": "Ongoing" | "Resolved",
 *       "dateFound": "YYYY-MM-DD",
 *       "estimatedCost": number,
 *       "assignedTechnician": "string",
 *       "dueDate": "YYYY-MM-DD",
 *       "photos": [
 *         {
 *           "id": "string",
 *           "url": "string"
 *         }
 *       ]
 *     }
 *   ],
 *   "inspections": [
 *     {
 *       "id": "string",
 *       "equipmentId": "string",
 *       "inspectionDate": "YYYY-MM-DD",
 *       "technicianName": "string",
 *       "inspectionType": "PM" | "Emergency" | "Repair",
 *       "findings": "string",
 *       "workPerformed": "string",
 *       "attachments": [
 *         {
 *           "id": "string",
 *           "url": "string"
 *         }
 *       ],
 *       "nextDueDate": "YYYY-MM-DD"
 *     }
 *   ],
 *   "equipmentCounts": {
 *     "boilers": number,
 *     "chillers": number,
 *     "ahus": number,
 *     "pumps": number,
 *     "towers": number,
 *     "exchangers": number
 *   },
 *   "equipment": [
 *     {
 *       "id": "string",
 *       "name": "string",
 *       "type": "boiler" | "chiller" | "ahu" | "pump" | "generator" | "misc",
 *       "manufacturer": "string",
 *       "model": "string",
 *       "serialNumber": "string",
 *       "installDate": "YYYY-MM-DD",
 *       "location": "string",
 *       "photoUrl": "string" (URL to equipment photo),
 *       "specifications": {
 *         // Object with type-specific specs (e.g., capacity, fuel, airflow, etc.)
 *         // Structure varies by equipment type
 *       },
 *       "healthScore": number (0-100),
 *       "issuesCount": number,
 *       "lastServiceDate": "string" (formatted date like "Oct 18, 2025" or "No recent service")
 *     }
 *   ]
 * }
 */
export async function getMechanicalOverview(buildingId) {
  const token = localStorage.getItem('managerToken');
  
  console.log('[getMechanicalOverview] Fetching overview for buildingId:', buildingId);
  console.log('[getMechanicalOverview] API URL:', `${API_BASE_URL}/api/mechanical/overview`);
  
  const response = await fetch(`${API_BASE_URL}/api/mechanical/overview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    },
    body: JSON.stringify({ buildingId })
  });

  if (!response.ok) {
    console.error('[getMechanicalOverview] Request failed:', response.status, response.statusText);
    throw new Error(`Failed to fetch mechanical overview: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  console.log('[getMechanicalOverview] Raw overview response:', data);
  console.log('[getMechanicalOverview] Equipment count from API:', Array.isArray(data?.equipment) ? data.equipment.length : 'N/A');
  
  return data;
}

/**
 * Get detailed information for a specific equipment item
 * 
 * Endpoint: POST /api/mechanical/equipment/detail
 * 
 * Request Format:
 * {
 *   "equipmentId": "string" (required) - The equipment ID (Notion page ID)
 *   "buildingId": "string" (required) - The building ID
 * }
 * 
 * Headers:
 * - Content-Type: application/json
 * - Authorization: Bearer {token} (required - uses @token_required decorator)
 * 
 * Response Format:
 * {
 *   "id": "string",
 *   "name": "string",
 *   "type": "boiler" | "chiller" | "ahu" | "pump" | "generator" | "misc",
 *   "photoUrl": "string" (URL to equipment cover photo),
 *   "photoArray": ["string"] (optional — additional image URLs; carousel shows photoUrl first, then these),
 *   "statusTag": "Good" | "Needs Service" | "Critical",
 *   "location": "string",
 *   "quantity": number (quantity of equipment),
 *   "details": "string" (equipment details),
 *   "manufacturer": "string",
 *   "model": "string",
 *   "serialNumber": "string",
 *   "year": "string" (year as string),
 *   "installDate": "YYYY-MM-DD",
 *   "lastPmDate": "YYYY-MM-DD",
 *   "nextDueDate": "YYYY-MM-DD",
 *   "aiScorePercent": number (0-100),
 *   "aiScoreBreakdown": "string" (description of how score is calculated),
 *   "issues": [
 *     {
 *       "id": "string",
 *       "title": "string",
 *       "priority": "Low" | "Medium" | "High",
 *       "status": "Ongoing" | "Resolved",
 *       "estimatedCost": number,
 *       "dateFound": "YYYY-MM-DD",
 *       "details": "string" (detailed description),
 *       "photos": [
 *         {
 *           "id": "string",
 *           "label": "string" (e.g., "img"),
 *           "url": "string"
 *         }
 *       ]
 *     }
 *   ],
 *   "serviceHistory": [
 *     {
 *       "id": "string",
 *       "date": "YYYY-MM-DD",
 *       "type": "PM" | "Repair" | "Emergency" | "Inspection",
 *       "technician": "string",
 *       "findings": "string",
 *       "details": "string" (detailed description of work),
 *       "attachments": [
 *         {
 *           "id": "string",
 *           "label": "string" (e.g., "img"),
 *           "url": "string"
 *         }
 *       ],
 *       "equipmentName": "string"
 *     }
 *   ]
 * }
 */
export async function getEquipmentDetail(equipmentId, buildingId) {
  const token = localStorage.getItem('managerToken');
  
  if (!buildingId) {
    throw new Error('buildingId is required to fetch equipment detail');
  }
  
  const url = `${API_BASE_URL}/api/mechanical/equipment/detail`;
  console.log('[getEquipmentDetail] Request:', { method: 'POST', url, body: { equipmentId, buildingId } });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    },
    body: JSON.stringify({ equipmentId, buildingId })
  });

  if (!response.ok) {
    console.error('[getEquipmentDetail] Error response:', response.status, response.statusText);
    throw new Error(`Failed to fetch equipment detail: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  // Full payload for the /equipment top card (EquipmentInfo: photo, location, model, dates, etc.)
  console.log('[getEquipmentDetail] Raw API response (equipment detail):', data);
  try {
    console.log('[getEquipmentDetail] Response JSON (stringified):', JSON.stringify(data, null, 2));
  } catch (e) {
    console.warn('[getEquipmentDetail] Could not stringify response', e);
  }

  return data;
}

/**
 * Get all issues across all equipment for a building
 * 
 * Endpoint: POST /api/mechanical/issues/all
 * 
 * Request Format:
 * {
 *   "buildingId": "string" (required) - The building ID
 * }
 * 
 * Headers:
 * - Content-Type: application/json
 * - Authorization: Bearer {token} (optional, if auth is required)
 * 
 * Response Format:
 * [
 *   {
 *     "id": "string",
 *     "equipmentId": "string",
 *     "equipmentName": "string",
 *     "title": "string",
 *     "priority": "Low" | "Medium" | "High",
 *     "status": "Ongoing" | "Resolved",
 *     "estimatedCost": number,
 *     "dateFound": "YYYY-MM-DD",
 *     "details": "string" (detailed description),
 *     "photos": [
 *       {
 *         "id": "string",
 *         "label": "string",
 *         "url": "string"
 *       }
 *     ]
 *   }
 * ]
 * 
 * Note: Issues should be sorted by dateFound in descending order (most recent first)
 */
export async function getAllIssues(buildingId) {
  const token = localStorage.getItem('managerToken');
  
  console.log('[getAllIssues] Fetching issues for buildingId:', buildingId);
  console.log('[getAllIssues] API URL:', `${API_BASE_URL}/api/mechanical/issues/all`);
  
  const response = await fetch(`${API_BASE_URL}/api/mechanical/issues/all`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    },
    body: JSON.stringify({ buildingId })
  });

  if (!response.ok) {
    console.error('[getAllIssues] Request failed:', response.status, response.statusText);
    throw new Error(`Failed to fetch all issues: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  console.log('[getAllIssues] Raw response data:', data);
  console.log('[getAllIssues] Number of issues fetched:', Array.isArray(data) ? data.length : 'Not an array');
  
  // Helper function to extract date string from date object or string
  const getDateString = (dateValue) => {
    if (!dateValue) return null;
    if (typeof dateValue === 'string') return dateValue;
    if (typeof dateValue === 'object' && dateValue.iso) return dateValue.iso;
    return null;
  };
  
  // Ensure issues are sorted by date (most recent first)
  if (Array.isArray(data)) {
    console.log('[getAllIssues] Processing issues array...');
    data.forEach((issue, index) => {
      console.log(`[getAllIssues] Issue ${index + 1}:`, {
        id: issue.id,
        title: issue.title,
        equipmentId: issue.equipmentId,
        equipmentName: issue.equipmentName,
        priority: issue.priority,
        status: issue.status,
        dateFound: issue.dateFound,
        dateFoundType: typeof issue.dateFound,
        dateFoundExtracted: getDateString(issue.dateFound)
      });
    });
    
    const sorted = data.sort((a, b) => {
      const dateA = getDateString(a.dateFound);
      const dateB = getDateString(b.dateFound);
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1; // Put items without dates at the end
      if (!dateB) return -1;
      return new Date(dateB) - new Date(dateA);
    });
    
    console.log('[getAllIssues] Issues sorted by date (most recent first)');
    console.log('[getAllIssues] Final sorted issues:', sorted);
    
    return sorted;
  }
  
  console.log('[getAllIssues] Response is not an array, returning as-is');
  return data;
}

/**
 * Update an issue status (Ongoing / Resolved)
 * Endpoint: POST /api/mechanical/issues/update-status
 */
export async function updateIssueStatus(issueId, status) {
  const token = localStorage.getItem('managerToken');
  const response = await fetch(`${API_BASE_URL}/api/mechanical/issues/update-status`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    },
    body: JSON.stringify({ issueId, status })
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to update issue status: ${response.status} ${text}`);
  }

  return await response.json();
}

/**
 * Get all maintenance history across all equipment for a building
 * 
 * Endpoint: POST /api/mechanical/maintenance/history
 * 
 * Request Format:
 * {
 *   "buildingId": "string" (required) - The building ID
 * }
 * 
 * Headers:
 * - Content-Type: application/json
 * - Authorization: Bearer {token} (optional, if auth is required)
 * 
 * Response Format:
 * [
 *   {
 *     "id": "string",
 *     "equipmentId": ["string"] (array of equipment IDs),
 *     "equipmentName": ["string"] (array of equipment names),
 *     "date": "YYYY-MM-DD",
 *     "type": "PM" | "Repair" | "Emergency" | "Inspection",
 *     "technician": "string",
 *     "findings": "string",
 *     "details": "string" (detailed description of work performed),
 *     "attachments": [
 *       {
 *         "id": "string",
 *         "label": "string" (e.g., "img"),
 *         "url": "string"
 *       }
 *     ]
 *   }
 * ]
 * 
 * Note: Maintenance history should be sorted by date in descending order (most recent first)
 */
export async function getAllMaintenanceHistory(buildingId) {
  const token = localStorage.getItem('managerToken');
  
  console.log('[getAllMaintenanceHistory] Fetching maintenance history for buildingId:', buildingId);
  console.log('[getAllMaintenanceHistory] API URL:', `${API_BASE_URL}/api/mechanical/maintenance/history`);
  
  const response = await fetch(`${API_BASE_URL}/api/mechanical/maintenance/history`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    },
    body: JSON.stringify({ buildingId })
  });

  if (!response.ok) {
    console.error('[getAllMaintenanceHistory] Request failed:', response.status, response.statusText);
    throw new Error(`Failed to fetch maintenance history: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  console.log('[getAllMaintenanceHistory] Raw response data:', data);
  console.log('[getAllMaintenanceHistory] Number of maintenance visits fetched:', Array.isArray(data) ? data.length : 'Not an array');
  
  // Helper function to extract date string from date object or string
  const getDateString = (dateValue) => {
    if (!dateValue) return null;
    if (typeof dateValue === 'string') return dateValue;
    if (typeof dateValue === 'object' && dateValue.iso) return dateValue.iso;
    return null;
  };
  
  // Ensure history is sorted by date (most recent first)
  if (Array.isArray(data)) {
    console.log('[getAllMaintenanceHistory] Processing maintenance history array...');
    data.forEach((visit, index) => {
      console.log(`[getAllMaintenanceHistory] Visit ${index + 1}:`, {
        id: visit.id,
        equipmentId: visit.equipmentId,
        equipmentName: visit.equipmentName,
        date: visit.date,
        dateType: typeof visit.date,
        isObject: typeof visit.date === 'object',
        hasIso: visit.date?.iso,
        dateExtracted: getDateString(visit.date),
        type: visit.type,
        technician: visit.technician
      });
    });
    
    const sorted = data.sort((a, b) => {
      const dateA = getDateString(a.date);
      const dateB = getDateString(b.date);
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1; // Put items without dates at the end
      if (!dateB) return -1;
      return new Date(dateB) - new Date(dateA);
    });
    
    console.log('[getAllMaintenanceHistory] Maintenance history sorted by date (most recent first)');
    console.log('[getAllMaintenanceHistory] Final sorted maintenance history:', sorted);
    
    return sorted;
  }
  
  console.log('[getAllMaintenanceHistory] Response is not an array, returning as-is');
  return data;
}

/**
 * Generate and download PDF report for an issue
 * 
 * Endpoint: POST /api/mechanical/issues/report-pdf
 * 
 * Request Format:
 * {
 *   "issueId": "string" (required) - The issue ID
 *   "buildingName": "string" (optional) - Building name for the report
 * }
 * 
 * Returns: PDF file download
 */
export async function downloadIssueReportPdf(issueId, buildingName = '') {
  const token = localStorage.getItem('managerToken');
  
  console.log('[downloadIssueReportPdf] Generating PDF for issue:', issueId);
  
  const response = await fetch(`${API_BASE_URL}/api/mechanical/issues/report-pdf`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    },
    body: JSON.stringify({ issueId, buildingName })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to generate PDF: ${response.status} ${response.statusText}`);
  }

  // Get the PDF blob
  const blob = await response.blob();
  
  // Get filename from Content-Disposition header or use default
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'Issue_Report.pdf';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }
  
  // Create download link and trigger download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  
  console.log('[downloadIssueReportPdf] PDF downloaded:', filename);
  
  return { success: true, filename };
}


/**
 * Download a maintenance/visit report as PDF
 * 
 * Request body:
 * {
 *   "visitId": "string" (optional) - The objectId of the visit in Back4App
 *   "buildingName": "string" (optional) - Building name for the report
 *   "visitData": {
 *     "date": "string",
 *     "technician": "string",
 *     "type": "string",
 *     "findings": "string",
 *     "details": "string",
 *     "equipmentNames": "string" or array,
 *     "attachments": array
 *   }
 * }
 * 
 * Returns: PDF file download
 */
export async function downloadMaintenanceReportPdf(visitId, buildingName = '', visitData = {}) {
  const token = localStorage.getItem('managerToken');
  
  console.log('[downloadMaintenanceReportPdf] Generating PDF for visit:', visitId || 'inline data');
  
  const response = await fetch(`${API_BASE_URL}/api/mechanical/maintenance/report-pdf`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    },
    body: JSON.stringify({ visitId, buildingName, visitData })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to generate PDF: ${response.status} ${response.statusText}`);
  }

  // Get the PDF blob
  const blob = await response.blob();
  
  // Get filename from Content-Disposition header or use default
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'Maintenance_Report.pdf';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }
  
  // Create download link and trigger download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  
  console.log('[downloadMaintenanceReportPdf] PDF downloaded:', filename);
  
  return { success: true, filename };
}


/**
 * Request a quote for an issue - sends email to manager and Hudson HVAC team
 * 
 * Request body:
 * {
 *   "manager_email": "string" - Manager's email address
 *   "issue_link": "string" - Shareable link to the issue
 *   "building": "string" - Building name/address
 *   "issue_title": "string" - Title/name of the issue
 * }
 * 
 * Returns: { success: true, message: "..." } on success
 */
export async function requestQuote(managerEmail, issueLink, buildingName, issueTitle) {
  const token = localStorage.getItem('managerToken');
  
  console.log('[requestQuote] Sending quote request for:', buildingName, issueTitle);
  
  const response = await fetch(`${API_BASE_URL}/api/mechanical/request-quote`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    },
    body: JSON.stringify({
      manager_email: managerEmail,
      issue_link: issueLink,
      building: buildingName,
      issue_title: issueTitle
    })
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || `Failed to request quote: ${response.status}`);
  }
  
  console.log('[requestQuote] Quote request sent successfully');
  
  return data;
}


/**
 * Submit a priority service request - sends email to manager and Hudson HVAC team
 * 
 * Request body:
 * {
 *   "phone": "string" - Contact phone number
 *   "description": "string" - Description of the issue
 *   "building": "string" - Building name/address
 *   "manager_email": "string" - Manager's email address
 * }
 * 
 * Returns: { success: true, message: "..." } on success
 */
export async function submitPriorityServiceRequest(phone, description, buildingName, managerEmail) {
  const token = localStorage.getItem('managerToken');
  
  console.log('[submitPriorityServiceRequest] Sending priority service request for:', buildingName);
  
  const response = await fetch(`${API_BASE_URL}/api/mechanical/priority-service-request`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    },
    body: JSON.stringify({
      phone,
      description,
      building: buildingName,
      manager_email: managerEmail
    })
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || `Failed to submit priority service request: ${response.status}`);
  }
  
  console.log('[submitPriorityServiceRequest] Request sent successfully');
  
  return data;
}

